import { BookOpen, ExternalLink, Terminal } from 'lucide-react'
import { Button } from '@deepguide-ai/ui/button'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import { CopyButton } from '@/components/animate-ui/buttons/copy'
import { SERVER_URL } from '@/lib/env'

const baseUrl = `${SERVER_URL}/api/audit/v1`
const firstRequest = `curl --fail-with-body \\\n  -H "Authorization: Bearer $AUDIT_API_TOKEN" \\\n  "${baseUrl}/cycles"`
const listEvidence = `curl --fail-with-body \\\n  -H "Authorization: Bearer $AUDIT_API_TOKEN" \\\n  "${baseUrl}/cycles/$AUDIT_CYCLE_ID/evidence?kind=file&limit=50"`
const downloadEvidence = `curl --fail \\\n  -H "Authorization: Bearer $AUDIT_API_TOKEN" \\\n  "${SERVER_URL}$CONTENT_PATH" \\\n  --output evidence.bin`

export function AuditorApiGuide() {
  return (
    <section aria-label='Auditor API guide' className='min-w-0 space-y-6'>
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div className='max-w-2xl'>
          <h2 className='text-foreground text-xl font-semibold'>Connect your audit tools</h2>
          <p className='text-muted-foreground mt-2 text-sm leading-6'>
            Pull evidence, policies, and test statuses into your firm’s scripts and workpaper tools.
            Schedule reads with your own tools, or use an explicitly write-enabled token to post comments and Control conclusions.
          </p>
        </div>
        <Button variant='outline' size='sm' asChild>
          <a href={`${SERVER_URL}/api/audit/openapi.json`} target='_blank' rel='noopener noreferrer'>
            <BookOpen className='size-4' />
            API reference (OpenAPI)
            <ExternalLink className='size-3.5' />
          </a>
        </Button>
      </div>
      <div className='grid min-w-0 gap-6 lg:grid-cols-2'>
        <div className='space-y-3 text-sm leading-6'>
          <h3 className='text-foreground font-medium'>Your access scope</h3>
          <p className='text-muted-foreground'>
            Each token belongs to your auditor seat in this workspace. It can read all audit cycles
            assigned to you, across frameworks. Unassigned cycles, other workspaces, and other
            workspace data stay outside its access.
          </p>
          <p className='text-muted-foreground'>
            Access follows your current cycle assignments. Your customer can see and revoke your
            tokens. API activity is recorded in the auditor activity log. Existing tokens remain read-only.
          </p>
          <p className='text-muted-foreground'>
            For a one-off ZIP, use <span className='text-foreground font-medium'>Download Audit Package</span> inside
            an audit. The API downloads existing packages; generate a fresh package in the portal first.
          </p>
        </div>
        <div className='min-w-0 rounded-md border bg-card p-4'>
          <Label htmlFor='auditor-api-base-url'>API base URL</Label>
          <div className='mt-2 flex min-w-0 items-center gap-2'>
            <Input id='auditor-api-base-url' readOnly value={baseUrl} className='min-w-0 bg-background font-mono text-xs' />
            <CopyButton content={baseUrl} variant='outline' aria-label='Copy API base URL' />
          </div>
          <div className='mt-5 flex items-center justify-between gap-2'>
            <h3 className='text-foreground text-sm font-medium'>Your first request</h3>
            <CopyButton content={firstRequest} variant='ghost' aria-label='Copy first request' />
          </div>
          <p className='text-muted-foreground mb-3 text-xs leading-5'>
            Create a token below, then set AUDIT_API_TOKEN to its value in your terminal or secret manager.
            This request lists the cycles you can access.
          </p>
          <pre className='overflow-x-auto rounded-md bg-muted p-3 text-xs leading-6'><code>{firstRequest}</code></pre>
          <p className='text-muted-foreground mt-3 text-xs'>Send the token in the Authorization header, never in a URL.</p>
        </div>
      </div>
      <div className='space-y-2 rounded-md border bg-card p-4 text-sm leading-6'>
        <h3 className='font-medium'>Write review work back</h3>
        <p className='text-muted-foreground'>Select Allow comments and Control conclusions when creating a new token. Every REST write requires an Idempotency-Key; reuse the same key and body after a timeout. Existing tokens and Auditor MCP remain read-only.</p>
        <p className='text-muted-foreground'>Comments use a visible Test id, policy slug or policy submission id. Read a Control’s current review revision before recording its conclusion. Completed audits stay read-only. Limits: 50 writes and 10 Control conclusions per minute per token.</p>
      </div>
      <details className='group rounded-md border bg-card'>
        <summary className='cursor-pointer rounded-md px-4 py-3 text-sm font-medium focus-visible:outline-2 focus-visible:outline-ring'>
          <span className='inline-flex items-center gap-2'><Terminal className='size-4' />Quickstart: download evidence</span>
        </summary>
        <div className='min-w-0 space-y-5 border-t px-4 py-5 text-sm leading-6'>
          <p className='text-muted-foreground'>
            Start with the request above. From <code>data.items</code>, choose a cycle and set
            <code> AUDIT_CYCLE_ID</code> to its <code>id</code>.
          </p>
          <div className='min-w-0'>
            <div className='mb-2 flex items-center justify-between gap-2'>
              <h3 className='font-medium'>1. List the cycle’s evidence files</h3>
              <CopyButton content={listEvidence} variant='ghost' aria-label='Copy evidence request' />
            </div>
            <pre className='overflow-x-auto rounded-md bg-muted p-3 text-xs leading-6'><code>{listEvidence}</code></pre>
            <p className='text-muted-foreground mt-2'>
              Files are in <code>data.groups[].items[]</code>. Set <code>CONTENT_PATH</code> to a file’s
              <code> content</code> path exactly as returned, including any query string. If
              <code> nextCursor</code> is not null, pass it as <code>cursor</code> to read the next page.
            </p>
          </div>
          <div className='min-w-0'>
            <div className='mb-2 flex items-center justify-between gap-2'>
              <h3 className='font-medium'>2. Download and verify the file</h3>
              <CopyButton content={downloadEvidence} variant='ghost' aria-label='Copy download request' />
            </div>
            <pre className='overflow-x-auto rounded-md bg-muted p-3 text-xs leading-6'><code>{downloadEvidence}</code></pre>
            <p className='text-muted-foreground mt-2'>
              The file is saved as <code>evidence.bin</code>. Run <code>shasum -a 256 evidence.bin</code>.
              The digest must match the listing’s
              <code> contentSha256</code> and the download’s <code>X-Content-Sha256</code> header.
              For an existing ZIP, list <code>/cycles/&#123;id&#125;/packages</code> and use its <code>content</code> path the same way.
            </p>
          </div>
          <p className='text-muted-foreground'>
            Limits: 600 data reads and 60 downloads per minute per token. On a 429 response, wait
            the number of seconds in <code>Retry-After</code>. See the API reference for policy content,
            submission details, filters, and response fields.
          </p>
        </div>
      </details>
    </section>
  )
}
